import { getDefinition, type DefLookup } from "./catalog";
import { withoutDanglingCables } from "./diagnostics/cable-endpoints";
import { buildGraph, v } from "./diagnostics/graph";
import { effectiveTerminals } from "./effective";
import { isSenseTerminal } from "./sense-terminal";
import { identifyShuntBank } from "./shunt-bank";
import type { System } from "./types";

const EMPTY: ReadonlySet<string> = new Set();
const cache = new WeakMap<System, WeakMap<DefLookup, WeakMap<readonly string[], ReadonlySet<string>>>>();

/** Wiring context for selected components, not a claim that the circuit is
 * powered. Follow only physical terminal connections: never bridge a load,
 * battery, converter or signal port to a different electrical domain. */
export function componentFocusCableIds(
  system: System,
  nodeIds: readonly string[],
  lookup: DefLookup = getDefinition,
): ReadonlySet<string> {
  if (nodeIds.length === 0) return EMPTY;
  let byLookup = cache.get(system);
  if (!byLookup) { byLookup = new WeakMap(); cache.set(system, byLookup); }
  let bySelection = byLookup.get(lookup);
  if (!bySelection) { bySelection = new WeakMap(); byLookup.set(lookup, bySelection); }
  const cached = bySelection.get(nodeIds);
  if (cached) return cached;

  const validSystem = withoutDanglingCables(system, lookup);
  const graph = buildGraph(validSystem, lookup, { excludeSense: true });
  const selected = new Set(nodeIds);
  const seeds = new Set<string>();
  for (const node of validSystem.nodes) {
    if (node.kind !== "component" || !selected.has(node.id)) continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    for (const terminal of effectiveTerminals(def, node)) {
      if (!isSenseTerminal(def, terminal)) seeds.add(v(node.id, terminal.id));
    }
    // A shunt's negative measuring posts describe only half of its bank.
    // Include the identified bank's supply rail without using AUX as a bus.
    const monitored = identifyShuntBank(validSystem, lookup, node.id);
    if (monitored) {
      for (const terminal of [...monitored.bank.topPositives, ...monitored.bank.bottomNegatives]) seeds.add(terminal);
    }
  }
  const result = new Set(validSystem.cables.filter(c => selected.has(c.fromNodeId) || selected.has(c.toNodeId)).map(c => c.id));
  const visited = new Set(seeds);
  const queue = [...seeds];
  for (let i = 0; i < queue.length; i++) {
    for (const edge of graph.adj.get(queue[i]) ?? []) {
      if (edge.via.kind === "cable") result.add(edge.via.cableId);
      if (visited.has(edge.vertex)) continue;
      visited.add(edge.vertex);
      queue.push(edge.vertex);
    }
  }
  bySelection.set(nodeIds, result);
  return result;
}
